import { useMemo, useCallback, useState } from "react";
import { message } from "antd";
import { useSelector, useDispatch } from "react-redux";
import useUserPrivileges from "../../../hooks/useUserPrivileges";
import { getEnqMngtColumns } from "./getEnqMngtColumns";
import CustomTable from "../../../components/CustomTable/CustomTable";
import {
  TABLE_COLUMN_DEFAULTS,
  buildTableScreenKey,
} from "../../../constants/tableColumnDefaults";
import ActionReasonModal from "../../../components/ActionReasonModal/ActionReasonModal";
import {
  ENQUIRY_SUCCESS_MESSAGES,
  ENQUIRY_ACTIONS,
} from "../../../constants/constants";
import {
  rejectEnquiry,
  reopenEnquiry,
  assignEnquiry,
  activateEnquiry,
  getEnquiryManagementView,
  resendKycLink,
} from "../../../redux/slices/EnqMngtSlice";
import { useNavigate } from "react-router-dom";
import { count } from "../../../redux/slices/dealsSlice";
import { count as allCount } from "../../../redux/slices/AuthSlice";
import { ENQ_STATUS_PRIVILEGE_MAP } from "../../../constants/PermissionCodes";

const EnqMngtTable = (props) => {
  const { tableData, selectedStatus, onColumnControlChange } = props;
  const { userPrivileges } = useUserPrivileges(ENQ_STATUS_PRIVILEGE_MAP);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, commentSubmitError, commentSubmitLoading } = useSelector(
    (state) => state?.enqMngt,
  );
  const [modal, setModal] = useState({ type: null, data: null });

  const closeModal = useCallback(
    () => setModal({ type: null, data: null }),
    [],
  );

  // Unified handler for all actions
  const openModal = useCallback((type, org) => {
    setModal({ type, data: org });
  }, []);

  const handleAssignClick = useCallback(
    (org) => openModal("assign", org),
    [openModal],
  );

  const handleRejectClick = useCallback(
    (org) => openModal("reject", org),
    [openModal],
  );

  const handleReopenClick = useCallback(
    (org) => openModal("reopen", org),
    [openModal],
  );

  const handleActivateUserClick = useCallback(
    (org) => openModal("activate", org),
    [openModal],
  );

  const handleSendQuestionnaireClick = useCallback(
    (org) =>
      navigate(`/enquiry-management/send-questionnaire/${org.orgId}`, {
        state: { org, isCreditAssessment: false },
      }),
    [navigate],
  );

  const handleCompleteKYCClick = useCallback(
    (org) =>
      navigate(`/enquiry-management/complete-kyc/${org.orgId}`, {
        state: { dealCategoryId: org.dealCategoryId },
      }),
    [navigate],
  );

  const handleSendKycLink = useCallback(
    async (org) => {
      try {
        const res = await dispatch(
          resendKycLink({
            orgId: org.orgId,
            dealCategoryId: org.dealCategoryId,
          }),
        );

        if (res?.payload?.statusCode === 201) {
          message.success(ENQUIRY_SUCCESS_MESSAGES.resendKyc);
        } else {
          message.error(res?.payload?.message || "Failed to send KYC link");
        }
      } catch (error) {
        message.error("Failed to send KYC link");
      }
    },
    [dispatch],
  );

  const handleCreditAssessmentClick = useCallback(
    (org) =>
      navigate(`/enquiry-management/send-questionnaire/${org.orgId}`, {
        state: { org, isCreditAssessment: true },
      }),
    [navigate],
  );

  const handleView = (record) => {
    navigate(`/enquiry-management/enq-details/${record.orgId}`, {
      state: {
        org: record,
      },
    });
  };

  const handleViewBusinessDetails = useCallback(
    (org) => {
      navigate(`/enquiry-management/view-business/${org.orgId}`, {
        state: { org, selectedStatus },
      });
    },
    [navigate, selectedStatus],
  );

  const ACTION_MAP = useMemo(
    () => ({
      [ENQUIRY_ACTIONS.assign]: assignEnquiry,
      [ENQUIRY_ACTIONS.reject]: rejectEnquiry,
      [ENQUIRY_ACTIONS.reopen]: reopenEnquiry,
      [ENQUIRY_ACTIONS.activate]: activateEnquiry,
    }),
    [],
  );

  const handleReasonSubmit = useCallback(
    async (values) => {
      if (!modal.type || !modal.data?.orgId) return;
      const action = ACTION_MAP[modal.type];
      const res = await dispatch(
        action({
          orgId: modal.data.orgId,
          dealCategoryId: modal.data.dealCategoryId,
          comments: values.reason,
          modifiedAt: modal.data.modifiedAt,
          ...(modal.type === ENQUIRY_ACTIONS.assign && {
            entityTypeId: modal.data.entityTypeId || modal.data.businessTypeId,
          }),
        }),
      );

      if (res?.payload?.statusCode === 201) {
        message.success(
          ENQUIRY_SUCCESS_MESSAGES[modal.type](modal.data?.busName),
        );
        dispatch(getEnquiryManagementView(selectedStatus));
        dispatch(count("ENQUIRYMGMT"));
        dispatch(allCount("ALL"));
        closeModal();
      }
    },
    [dispatch, modal, closeModal, selectedStatus, ACTION_MAP],
  );

  // memoize columns
  const columns = useMemo(
    () =>
      getEnqMngtColumns({
        onView: handleView,
        onAssign: handleAssignClick,
        onReject: handleRejectClick,
        onReopen: handleReopenClick,
        onCompleteKYC: handleCompleteKYCClick,
        onSendKycLink: handleSendKycLink,
        onActivateUser: handleActivateUserClick,
        onSendQuestionnaire: handleSendQuestionnaireClick,
        onCreditAssessment: handleCreditAssessmentClick,
        onViewBusinessDetails: handleViewBusinessDetails,
        status: selectedStatus,
        privileges: userPrivileges,
      }),
    [
      handleAssignClick,
      handleRejectClick,
      handleReopenClick,
      handleCompleteKYCClick,
      handleSendKycLink,
      handleActivateUserClick,
      handleSendQuestionnaireClick,
      handleCreditAssessmentClick,
      handleViewBusinessDetails,
      handleView,
      selectedStatus,
      userPrivileges,
    ],
  );

  return (
    <>
      <CustomTable
        columns={columns}
        dataSource={tableData}
        loading={loading}
        rowKey={(record) => `${record.orgId}_${record.dealCategoryId}`}
        screenKey={buildTableScreenKey("enquiry")}
        defaultColumnKeys={TABLE_COLUMN_DEFAULTS.enquiry}
        onColumnControlChange={onColumnControlChange}
      />
      <ActionReasonModal
        modalType={modal.type}
        onClose={closeModal}
        onSubmit={handleReasonSubmit}
        submitLoading={commentSubmitLoading}
        submitError={commentSubmitError}
      />
    </>
  );
};

export default EnqMngtTable;
